/**
 * Data transformation utilities
 * Provides consistent data transformation patterns across the application
 */

import { STATUS, PAYMENT_STATUS, VALIDATION_CONSTANTS } from '../constants/appConstants';

/**
 * Formats a MongoDB ObjectId into a user-friendly Trainer ID
 * Format: TR-{last6chars}
 * Example: "690fd864170f7749edc69348" => "TR-93EDC6"
 * 
 * @param {string} objectId - MongoDB ObjectId
 * @param {string} name - Trainer's full name (optional, kept for backward compatibility but not used)
 * @returns {string} - User-friendly Trainer ID
 */
export const formatTrainerId = (objectId, name = '') => {
  if (!objectId) return 'N/A';
  
  // Extract last 6 characters from ObjectId (uppercase)
  const idStr = typeof objectId === 'string' ? objectId : objectId.toString();
  const last6Chars = idStr.slice(-6).toUpperCase();
  
  // Format: TR-{last6chars}
  return `TR-${last6Chars}`;
};

/**
 * Formats a MongoDB ObjectId into a user-friendly Member ID
 * Format: MB-{last6chars}
 * Example: "507f1f77bcf86cd799439011" => "MB-394390"
 * 
 * @param {string} objectId - MongoDB ObjectId
 * @param {string} name - Member's full name (optional, kept for backward compatibility but not used)
 * @returns {string} - User-friendly Member ID
 */
export const formatMemberId = (objectId, name = '') => {
  if (!objectId) return 'N/A';
  
  // Extract last 6 characters from ObjectId (uppercase)
  const idStr = typeof objectId === 'string' ? objectId : objectId.toString();
  const last6Chars = idStr.slice(-6).toUpperCase();
  
  // Format: MB-{last6chars}
  return `MB-${last6Chars}`;
};

/**
 * Transforms student data from API response to component state
 * @param {Object|Array} data - API response data
 * @returns {Array} - Transformed student array
 */
export const transformStudentData = (data, programsMap = null) => {
  const studentsArray = Array.isArray(data) 
    ? data 
    : (data?.data && Array.isArray(data.data) ? data.data : []);
  
  return studentsArray.map(student => {
    // Extract program details - handle both programDetails and program fields
    const programData = student?.programDetails || student?.program || {};
    
    // Get price - prefer finalPrice, fallback to price
    let price = Number(programData?.finalPrice || programData?.price || 0);
    
    // If price is 0 or null and we have a programsMap, try to get the program's current price
    // This handles cases where student was enrolled before program pricing was set
    if ((price === 0 || !price) && programsMap) {
      // Try to get programId from various possible locations
      let programId = null;
      if (student?.programId) {
        programId = typeof student.programId === 'object' 
          ? (student.programId._id?.toString() || student.programId.toString())
          : student.programId.toString();
      } else if (programData?.programId) {
        programId = typeof programData.programId === 'object'
          ? (programData.programId._id?.toString() || programData.programId.toString())
          : programData.programId.toString();
      }
      
      if (programId) {
        // Try exact match first
        let program = programsMap.get(programId);
        
        // If not found, try matching by name (case-insensitive) as fallback
        if (!program && programData?.name) {
          for (const [id, prog] of programsMap.entries()) {
            if (prog.name && prog.name.toLowerCase() === programData.name.toLowerCase()) {
              program = prog;
              break;
            }
          }
        }
        
        if (program) {
          // Use price field first, with fallback to legacy fields for migration compatibility
          price = Number(program.price || program.currentPrice || program.basePrice || 0);
          console.log(`Price lookup for program ${programId}: found price ${price} (currentPrice: ${program.currentPrice}, basePrice: ${program.basePrice}, price: ${program.price})`);
        } else {
          console.warn(`Program not found in map for ID: ${programId}, name: ${programData?.name}`);
        }
      } else {
        console.warn(`No programId found for student: ${student.name || 'Unknown'}`);
      }
    }
    
    return {
      _id: student?._id || student?.planId || '', // Student document _id (for database queries)
      planId: student?.planId || student?._id || '',
      studentId: student?.studentId || student?.userId || '', // User _id (for user-related queries)
      name: student?.name || 'Unknown',
      email: student?.email || '',
      phone: student?.phone || student?.mobile || '',
      status: student?.status || STATUS.ACTIVE,
      progress: Number(student?.progress || 0),
      totalSessions: Number(student?.totalSessions || 0),
      attendedSessions: Number(student?.attendedSessions || 0),
      lastSession: student?.lastSession || null,
      image: student?.image || student?.profileImage || null,
      programDetails: {
        name: programData?.name || 'N/A',
        price: price,
        duration: programData?.duration || 'N/A',
        programId: programData?.programId || student?.programId || null
      },
      programId: programData?.programId || student?.programId || null,
      paymentPlanId: student?.paymentPlanId || null,
      paymentPlanName: student?.paymentPlanName || null,
      payment: student?.payment || {
        status: student?.paymentStatus || PAYMENT_STATUS.PENDING,
        totalAmount: price,
        paidAmount: 0,
        dueAmount: price
      },
      paymentStatus: student?.paymentStatus || PAYMENT_STATUS.PENDING,
      review: student?.review || null,
      joinDate: student?.joinDate || student?.createdAt || new Date().toISOString(),
      isActive: student?.isActive ?? true,
    };
  });
};

/**
 * Transforms program data from API response to component state
 * @param {Object|Array} data - API response data
 * @returns {Array} - Transformed program array
 */
export const transformProgramData = (data) => {
  const programsArray = Array.isArray(data) 
    ? data 
    : (data?.data && Array.isArray(data.data) ? data.data : []);
  
  return programsArray.map(program => ({
    _id: program?._id || '',
    name: program?.name || 'Unnamed Program',
    description: program?.description || '',
    price: Number(program?.price || program?.currentPrice || program?.basePrice || 0),
    // Keep legacy fields for backward compatibility during migration
    basePrice: Number(program?.basePrice || program?.price || 0),
    currentPrice: Number(program?.currentPrice || program?.price || 0),
    duration: program?.duration || '',
    durationUnit: program?.durationUnit || '',
    classesPerDuration: program?.classesPerDuration || 0,
    isActive: program?.isActive ?? true,
    category: program?.category || '',
    image: program?.image || null,
  }));
};

/**
 * Transforms transaction data from API response to component state
 * @param {Object|Array} data - API response data
 * @returns {Array} - Transformed transaction array
 */
export const transformTransactionData = (data) => {
  // Handle different response formats
  let transactionsArray = [];
  
  if (Array.isArray(data)) {
    transactionsArray = data;
  } else if (data?.data && Array.isArray(data.data)) {
    transactionsArray = data.data;
  } else if (data?.success && Array.isArray(data.data)) {
    transactionsArray = data.data;
  }
  
  // Return empty array if no valid data
  if (!transactionsArray || transactionsArray.length === 0) {
    return [];
  }
  
  return transactionsArray.map(transaction => {
    // Handle populated objects (before they're converted to strings)
    const programName = transaction?.programId?.name || 
                       transaction?.programName || 
                       (typeof transaction?.programId === 'object' && transaction.programId?.name) ||
                       'Unknown Program';
    const studentName = transaction?.studentId?.name || 
                       transaction?.studentName || 
                       (typeof transaction?.studentId === 'object' && transaction.studentId?.name) ||
                       'Unknown Student';
    
    // Extract IDs (handle both string and object formats)
    const programId = typeof transaction?.programId === 'object' 
      ? (transaction.programId?._id?.toString() || transaction.programId?.toString() || '')
      : (transaction?.programId || '');
    const studentId = typeof transaction?.studentId === 'object'
      ? (transaction.studentId?._id?.toString() || transaction.studentId?.toString() || '')
      : (transaction?.studentId || '');
    
    return {
      _id: transaction?._id || '',
      studentId: studentId,
      studentName: studentName,
      programId: programId,
      programName: programName,
      amount: Number(transaction?.amount || transaction?.baseAmount || 0),
      finalAmount: Number(transaction?.finalAmount || transaction?.amount || transaction?.baseAmount || 0),
      paymentMethod: transaction?.paymentMethod || 'card',
      status: transaction?.status || PAYMENT_STATUS.PENDING,
      dueDate: transaction?.dueDate || null,
      paidDate: transaction?.paidDate || transaction?.date || null,
      lateFee: Number(transaction?.lateFee || 0),
      invoiceNo: transaction?.invoiceNo || '',
      discount: Number(transaction?.discount || 0),
      createdAt: transaction?.createdAt || transaction?.date || new Date().toISOString(),
    };
  });
};

/**
 * Safely converts value to number with default
 * @param {any} value - Value to convert
 * @param {number} defaultValue - Default value if conversion fails
 * @returns {number} - Converted number or default
 */
export const safeToNumber = (value, defaultValue = 0) => {
  const num = Number(value);
  return isNaN(num) ? defaultValue : num;
};

/**
 * Safely gets nested property from object
 * @param {Object} obj - Object to get property from
 * @param {string} path - Dot-separated path to property
 * @param {any} defaultValue - Default value if property doesn't exist
 * @returns {any} - Property value or default
 */
export const safeGet = (obj, path, defaultValue = null) => {
  const keys = path.split('.');
  let result = obj;
  
  for (const key of keys) {
    if (result == null || typeof result !== 'object') {
      return defaultValue;
    }
    result = result[key];
  }
  
  return result !== undefined ? result : defaultValue;
};

/**
 * Normalizes API response to consistent format
 * @param {any} response - API response
 * @returns {Object} - Normalized response with data, success, and message
 */
export const normalizeApiResponse = (response) => {
  if (Array.isArray(response)) {
    return {
      data: response,
      success: true,
      message: null,
    };
  }
  
  if (response?.data !== undefined) {
    return {
      data: response.data,
      success: response.success !== false,
      message: response.message || null,
    };
  }
  
  return {
    data: response,
    success: true,
    message: null,
  };
};

/**
 * Formats a name string to proper title case
 * Converts "vimal kumar" to "Vimal Kumar"
 * Handles edge cases: empty strings, multiple spaces, null/undefined values
 * 
 * @param {string} name - Name string (may be lowercase or mixed case)
 * @returns {string} - Properly formatted name in title case
 */
export const formatName = (name) => {
  if (!name || typeof name !== 'string') return '';
  
  return name
    .trim()
    .split(/\s+/) // Split on one or more whitespace characters
    .map(word => {
      if (!word) return '';
      // Preserve "AI" abbreviation
      if (word.toLowerCase() === 'ai') return 'AI';
      if (word.toLowerCase() === '(ai') return '(AI';
      if (word.toLowerCase() === 'ai)') return 'AI)';
      if (word.toLowerCase() === '(ai)') return '(AI)';
      // Capitalize first letter, lowercase the rest
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .filter(word => word.length > 0) // Remove empty strings
    .join(' ');
};

/**
 * Formats text to title case (for non-name fields like program names, services, etc.)
 * Similar to formatName but can be used for any text field
 * 
 * @param {string} text - Text string to format
 * @returns {string} - Formatted text in title case
 */
export const toTitleCase = (text) => {
  if (!text || typeof text !== 'string') return '';
  return formatName(text);
};

